import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff, FiMail, FiLock } from "react-icons/fi";

const BASE_API =
  process.env.BASE_API_URL ||
  "https://scanmymenu-nm38.onrender.com/api";

/*
=========================================
PLAN → DASHBOARD ROUTE MAP
=========================================
*/

const PLAN_ROUTES = {
  basic: "/owner/dashboard/basic/:restaurantId",
  pro:   "https://menu.proadmin.scanmymemory.in/restaurant/:restaurantId",
  max:   "/owner/dashboard/max/:restaurantId",
};

export default function OwnerLogin() {
  const navigate = useNavigate();

  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [message,      setMessage]      = useState("");
  const [loading,      setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      setLoading(true);

      /*
      ================================
      STEP 1 — LOGIN
      ================================
      */
      const res = await axios.post(
        `${BASE_API}/admin/owner/login`,
        { email, password }
      );

      if (!res.data.success) {
        setMessage(res.data.message || "Login failed");
        return;
      }

      /*
      SAVE AUTH DATA
      */
      localStorage.setItem("ownerToken",    res.data.token);
      localStorage.setItem("userRole",      res.data.role);
      localStorage.setItem("restaurantId",  res.data.restaurantId);

      const restaurantId = res.data.restaurantId;

      /*
      ================================
      STEP 2 — FETCH RESTAURANT
                to get plan
      ================================
      */
      const restaurantRes = await axios.get(
        `${BASE_API}/restaurants/restaurant/${restaurantId}`
      );

      const restaurant = restaurantRes.data?.restaurant;
      const plan       = restaurant?.plan;                    // "basic" | "pro" | "max" | undefined
      const status     = restaurant?.subscriptionStatus;      // "active" | other

      /*
      SAVE PLAN TO LOCALSTORAGE
      (useful for other pages)
      */
      localStorage.setItem("plan", plan || "none");

      /*
      ================================
      STEP 3 — REDIRECT BASED ON PLAN
      ================================
      */

    if (status !== "active" || !plan || !PLAN_ROUTES[plan]) {
  navigate("/owner/upgrade");
  return;
}

const targetRoute = PLAN_ROUTES[plan].replace(
  ":restaurantId",
  restaurantId
);

if (plan === "pro") {
  window.location.href = targetRoute;
} else {
  navigate(targetRoute);
}

    } catch (error) {
      setMessage(
        error.response?.data?.message || "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.overlay} />

      <form onSubmit={handleLogin} style={styles.form}>

        <h2 style={styles.title}>Welcome Back</h2>
        <p style={styles.subtitle}>Login to continue</p>

        {/* EMAIL */}
        <div style={styles.inputWrapper}>
          <FiMail style={styles.icon} />
          <input
            type="email"
            placeholder="Owner Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
          />
        </div>

        {/* PASSWORD */}
        <div style={styles.inputWrapper}>
          <FiLock style={styles.icon} />
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
          />
          <span
            onClick={() => setShowPassword(!showPassword)}
            style={styles.eye}
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </span>
        </div>

        <button
          style={loading ? styles.buttonDisabled : styles.button}
          disabled={loading}
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        {message && (
          <p style={styles.error}>{message}</p>
        )}

      </form>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
    padding: "20px",
    background: "linear-gradient(135deg,#ff6a00,#ff9966)",
  },
  overlay: {
    position: "absolute",
    width: "320px",
    height: "320px",
    background: "rgba(255,255,255,0.08)",
    borderRadius: "50%",
    filter: "blur(70px)",
  },
  form: {
    width: "100%",
    maxWidth: "360px",
    background: "rgba(255,255,255,0.15)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: "1px solid rgba(255,255,255,0.2)",
    padding: "28px 22px",
    borderRadius: "24px",
    boxShadow: "0 8px 35px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    zIndex: 10,
  },
  title: {
    textAlign: "center",
    color: "#fff",
    fontSize: "28px",
    fontWeight: "700",
    marginBottom: "6px",
  },
  subtitle: {
    textAlign: "center",
    color: "#f1f1f1",
    marginBottom: "24px",
    fontSize: "13px",
  },
  inputWrapper: {
    position: "relative",
    marginBottom: "16px",
  },
  icon: {
    position: "absolute",
    top: "50%",
    left: "14px",
    transform: "translateY(-50%)",
    color: "#888",
    fontSize: "17px",
  },
  input: {
    width: "100%",
    height: "52px",
    padding: "0 45px",
    borderRadius: "14px",
    border: "none",
    background: "#fff",
    fontSize: "15px",
    boxSizing: "border-box",
    outline: "none",
  },
  eye: {
    position: "absolute",
    right: "15px",
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    color: "#666",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
  },
  button: {
    width: "100%",
    height: "52px",
    border: "none",
    borderRadius: "14px",
    background: "linear-gradient(90deg,#111,#333)",
    color: "#fff",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "8px",
  },
  buttonDisabled: {
    width: "100%",
    height: "52px",
    border: "none",
    borderRadius: "14px",
    background: "#999",
    color: "#fff",
    cursor: "not-allowed",
    marginTop: "8px",
  },
  error: {
    marginTop: "14px",
    textAlign: "center",
    color: "#fff",
    background: "rgba(255,0,0,0.18)",
    padding: "10px",
    borderRadius: "10px",
    fontSize: "13px",
  },
};